import { useAuth } from "@/lib/auth.jsx";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, resolveMediaUrl } from "@/lib/api";
import { useEffect, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import ArtistSearch from "@/components/ArtistSearch";
import { toast } from "sonner";
import { Music, Heart, User, Upload, X } from "lucide-react";

const MAX_WISHES = 5;

export default function Profile() {
  const { user, refresh } = useAuth();
  const qc = useQueryClient();
  const { data: allArtists } = useQuery({ queryKey: ["all-artists"], queryFn: () => api.get("/artists", { params: { limit: 5000 } }).then(r => r.data) });
  const artistMap = Object.fromEntries((allArtists || []).map(a => [a.artist_id, a]));

  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [pictureUrl, setPictureUrl] = useState("");
  const [wishIds, setWishIds] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (user) {
      setDisplayName(user.display_name || user.name || "");
      setBio(user.bio || "");
      setPictureUrl(user.custom_picture_url || "");
      const wishes = Array.isArray(user.top_artist_wishes) ? user.top_artist_wishes : (user.top_artist_wish ? [user.top_artist_wish] : []);
      setWishIds(wishes);
    }
  }, [user]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Kun billeder"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload/profile-picture", form, { headers: { "Content-Type": "multipart/form-data" } });
      setPictureUrl(data.url);
      toast.success("Billede uploadet");
      await refresh();
      qc.invalidateQueries();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload fejlede");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const addWish = (artist) => {
    if (!artist) return;
    if (wishIds.includes(artist.artist_id)) { toast.error("Artist er allerede på din liste"); return; }
    if (wishIds.length >= MAX_WISHES) { toast.error(`Du kan højst vælge ${MAX_WISHES} artister`); return; }
    setWishIds([...wishIds, artist.artist_id]);
  };

  const removeWish = (id) => setWishIds(wishIds.filter(x => x !== id));

  const save = async () => {
    try {
      await api.patch("/auth/me", {
        display_name: displayName,
        bio,
        custom_picture_url: pictureUrl,
        top_artist_wishes: wishIds,
      });
      toast.success("Profil opdateret");
      await refresh();
      qc.invalidateQueries();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke gemme");
    }
  };

  const preview = resolveMediaUrl(pictureUrl) || user?.picture;
  const wishArtists = wishIds.map(id => artistMap[id]).filter(Boolean);
  const canAddMore = wishIds.length < MAX_WISHES;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="label-eyebrow">Min konto</div>
      <h1 className="hero-title text-4xl sm:text-5xl mt-2">Profil</h1>

      <div className="mt-10 card-flat p-6 sm:p-8">
        <h2 className="font-display font-bold text-2xl mb-6">Personlige oplysninger</h2>

        <div className="flex flex-col sm:flex-row items-start gap-6 mb-6">
          {preview ? (
            <img src={preview} alt="" data-testid="profile-picture-preview" className="w-24 h-24 rounded-full object-cover border border-white/10" onError={(e) => e.currentTarget.style.display = "none"} />
          ) : (
            <div className="w-24 h-24 rounded-full bg-zinc-800 flex items-center justify-center"><User size={28} className="text-zinc-500" /></div>
          )}
          <div className="flex-1 w-full">
            <Label className="label-eyebrow">Profilbillede</Label>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} data-testid="profile-picture-file" />
            <div className="mt-2 flex gap-2">
              <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="profile-picture-upload-btn">
                <Upload size={14} className="mr-2" /> {uploading ? "Uploader…" : (pictureUrl ? "Skift billede" : "Upload billede")}
              </Button>
              {pictureUrl && (
                <Button variant="ghost" onClick={() => setPictureUrl("")} data-testid="profile-picture-remove">Fjern</Button>
              )}
            </div>
            <div className="text-xs text-zinc-500 mt-2">Max 5MB, jpg/png/webp. Uden upload bruges dit Google-billede.</div>
          </div>
        </div>

        <div className="grid gap-4">
          <div>
            <Label className="label-eyebrow">Display navn</Label>
            <Input value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Dit navn i leaderboards" maxLength={40} data-testid="profile-name-input" />
          </div>
          <div>
            <Label className="label-eyebrow">Bio</Label>
            <Textarea value={bio} onChange={e => setBio(e.target.value)} placeholder="Fx 'Roskilde-veteran siden 2018'" maxLength={240} rows={3} data-testid="profile-bio-input" />
            <div className="text-xs text-zinc-500 mt-1">{bio.length}/240 tegn</div>
          </div>
        </div>
      </div>

      <div className="mt-8 card-flat p-6 sm:p-8" data-testid="profile-wishes-card">
        <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Heart size={16} className="text-pink-400" />
            <h2 className="font-display font-bold text-2xl">Dine topartist-ønsker</h2>
          </div>
          <div className="text-xs text-zinc-500" data-testid="profile-wishes-counter">{wishIds.length}/{MAX_WISHES} valgt</div>
        </div>
        <p className="text-sm text-zinc-400 mb-5">
          Hvem drømmer du om at se på en dansk festival? Bare for sjov – det tæller ikke i konkurrencen, men sender lidt god energi til de rigtige festivalsbookere.
        </p>

        {wishArtists.length > 0 && (
          <div className="space-y-3 mb-5">
            {wishArtists.map((a, i) => (
              <div key={a.artist_id} className="card-flat p-4 flex items-center gap-4" data-testid={`profile-wish-item-${i}`}>
                <div className="font-score text-xl text-zinc-500 w-6 text-center">{i + 1}</div>
                {a.image_url ? (
                  <img src={a.image_url} className="w-14 h-14 rounded-md object-cover" alt="" />
                ) : <div className="w-14 h-14 rounded-md bg-zinc-800 flex items-center justify-center"><Music size={18} className="text-zinc-500" /></div>}
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{a.name}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{a.is_danish ? "Dansk" : "International"}</div>
                </div>
                <button
                  className="text-zinc-500 hover:text-red-400 p-1"
                  onClick={() => removeWish(a.artist_id)}
                  aria-label={`Fjern ${a.name}`}
                  data-testid={`profile-wish-remove-${i}`}
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        )}

        {canAddMore ? (
          <ArtistSearch
            onSelect={addWish}
            testId="profile-wish-search"
            placeholder={wishArtists.length === 0 ? "Søg efter dit drømme-artist…" : "Tilføj endnu en artist…"}
          />
        ) : (
          <div className="text-sm text-zinc-500 border border-white/5 rounded-md p-3" data-testid="profile-wishes-full">
            Du har fyldt din liste med {MAX_WISHES} artister. Fjern en for at tilføje en ny.
          </div>
        )}
      </div>

      <div className="mt-8 flex justify-end sticky bottom-4">
        <Button onClick={save} className="bg-white text-black hover:bg-zinc-200 rounded-full px-8" data-testid="profile-save-btn">Gem profil</Button>
      </div>
    </div>
  );
}
